/** Шит «🧾 Покупка» (04.09, владелец: «сможет указать, что купил что-то
 *  нужное для нашей компании; фото/скриншоты, сколько денег потрачено, что
 *  куплено с возможностью выбрать из списка расходников»). Что — Dropdown по
 *  GET /api/op-purchases/catalog + «свой вариант» через TextPad; сумма —
 *  NumPadPop (A27: числа только кастомным нумпадом); фото до 6 (превью через
 *  objectURL, освобождаем); комментарий — TextPad. POST /api/op-purchases
 *  (multipart) → pending, подтверждает ОО; компенсация приходит вместе с ЗП.
 *  Цвет покупок — var(--lime). 04.09 (ревью): пока идёт POST, шит не
 *  закрывается; ответ применяется только к тому же открытию (см. genRef). */
import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { api } from "../../api/client";
import { ApiException } from "../../api/types";
import { Dropdown, NumPadPop, SheetDialog, TextPad } from "../../components/shared";
import { fmtDateApi } from "../../utils/date";
import css from "./dashActions.module.css";

interface CatalogItem { id: string; name: string; unit?: string | null }
interface Photo { file: File; url: string }
const MAX_PHOTOS = 6;
const MAX_BYTES = 20 * 1024 * 1024;

/** Понятный текст ошибки: 413/415 — лимиты фото на сервере, 503 — нет R2. */
function errText(e: unknown): string {
  if (e instanceof ApiException) {
    if (e.status === 413) return "Фото слишком большое — до 20 МБ на файл";
    if (e.status === 415) return "Такой формат не подходит — нужны изображения (jpg/png/webp)";
    if (e.status === 503) return "Хранилище фото недоступно — попробуй чуть позже";
    return e.body?.error || `Ошибка ${e.status}`;
  }
  return "Не удалось отправить — проверь связь";
}

export function PurchaseSheet({ open, date, onClose, onDone }: { open: boolean; date: Date; onClose: () => void; onDone: () => void }) {
  const [catalog, setCatalog] = useState<CatalogItem[] | null>(null);
  const [item, setItem] = useState("");
  const [consId, setConsId] = useState<string | null>(null);
  const [amount, setAmount] = useState(0);
  const [comment, setComment] = useState("");
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [dd, setDd] = useState<HTMLElement | null>(null);
  const [np, setNp] = useState<HTMLElement | null>(null);
  const [pad, setPad] = useState<"item" | "comment" | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const photosRef = useRef<Photo[]>([]);
  photosRef.current = photos;

  /* каталог — раз на первое открытие; без гейта прав (оператору /api/consumables закрыт) */
  useEffect(() => {
    if (!open || catalog) return;
    let alive = true;
    api<{ results?: CatalogItem[] }>("/api/op-purchases/catalog")
      .then((r) => { if (alive) setCatalog(r.results ?? []); })
      .catch(() => { if (alive) setCatalog([]); });
    return () => { alive = false; };
  }, [open, catalog]);

  /* 04.09 (ревью): «поколение» открытия + alive. Ответ POST применяем, только
   * если шит с тех пор не переоткрывался и компонент смонтирован — иначе
   * устаревший тост/ошибка и закрытие НОВОГО шита. Пока busy, закрыть шит
   * нельзя («Отмена» disabled, backdrop/Esc игнорируются) — без дубля покупки.
   * Сброс формы — при ОТКРЫТИИ, а не при закрытии: иначе поля мигают пустыми
   * во время 230мс анимации выезда. alive ставим в теле эффекта (StrictMode
   * в dev гоняет mount→cleanup→mount). objectURL превью освобождаем и на unmount. */
  const genRef = useRef(0);
  const aliveRef = useRef(true);
  useEffect(() => { aliveRef.current = true; return () => { aliveRef.current = false; }; }, []);
  useEffect(() => {
    if (!open) return;
    genRef.current += 1;
    photosRef.current.forEach((p) => URL.revokeObjectURL(p.url));
    setItem(""); setConsId(null); setAmount(0); setComment(""); setPhotos([]); setErr(null); setBusy(false);
    setDd(null); setNp(null); setPad(null);
  }, [open]);
  useEffect(() => () => photosRef.current.forEach((p) => URL.revokeObjectURL(p.url)), []);
  const dismiss = () => { if (!busy) onClose(); };

  const addFiles = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    setErr(null);
    const room = MAX_PHOTOS - photos.length;
    const ok: Photo[] = [];
    for (const f of files) {
      if (!f.type.startsWith("image/")) { setErr("Только изображения (фото, скриншоты)"); continue; }
      if (f.size > MAX_BYTES) { setErr(`«${f.name}» больше 20 МБ — не добавлено`); continue; }
      if (ok.length >= room) { setErr(`Максимум ${MAX_PHOTOS} фото`); break; }
      ok.push({ file: f, url: URL.createObjectURL(f) });
    }
    if (ok.length) setPhotos((p) => [...p, ...ok]);
  };
  const removePhoto = (i: number) => setPhotos((p) => { const x = p[i]; if (x) URL.revokeObjectURL(x.url); return p.filter((_, j) => j !== i); });

  const canSend = item.trim().length > 0 && amount > 0 && !busy;
  const send = async () => {
    if (!canSend) return;
    setBusy(true); setErr(null);
    const fd = new FormData();
    fd.append("amount", String(Math.round(amount)));
    fd.append("item", item.trim().slice(0, 120));
    if (consId) fd.append("consumable_id", consId);
    if (comment.trim()) fd.append("comment", comment.trim().slice(0, 500));
    fd.append("date", fmtDateApi(date));
    photos.forEach((p, i) => fd.append(`photo_${i}`, p.file, p.file.name));
    const gen = genRef.current;
    const live = () => aliveRef.current && genRef.current === gen; // 04.09 (ревью): то же открытие, компонент жив
    try {
      await api<{ ok: boolean; id: number; photos: number }>("/api/op-purchases", { method: "POST", body: fd, retries: 0 });
      if (live()) onDone();
    } catch (e) { if (live()) setErr(errText(e)); } finally { if (live()) setBusy(false); }
  };

  const unit = consId ? catalog?.find((c) => c.id === consId)?.unit : null;
  const d = fmtDateApi(date);
  return (
    <>
      <SheetDialog open={open} onDismiss={dismiss} ariaLabel="Покупка для компании" maxHeight="82dvh">
        <div className={css.sheetHead}>
          <span className={`${css.sheetTitle} ${css.tLime}`}>🧾 Покупка для компании</span>
          <span className={css.sheetDate}>{d.slice(8, 10)}.{d.slice(5, 7)}</span>
        </div>
        <div className={css.sheetBody}>
          <div className={css.field}>
            <span className={css.fLbl}>Что куплено</span>
            <button type="button" className={`${css.chip} ${item ? css.chipSet : css.chipEmpty}`} onClick={(e) => setDd(e.currentTarget)}>
              {item || "выбрать из расходников или вписать…"}{unit ? <span className={css.chipUnit}>{unit}</span> : null}
            </button>
          </div>
          <div className={css.field}>
            <span className={css.fLbl}>Сумма, ₽</span>
            <button type="button" className={`${css.chip} ${amount > 0 ? css.chipSet : css.chipEmpty}`} onClick={(e) => setNp(e.currentTarget)}>
              {amount > 0 ? <span className={css.money}>₽{amount.toLocaleString("ru")}</span> : "сколько потрачено…"}
            </button>
          </div>
          <div className={css.field}>
            <span className={css.fLbl}>Фото / скриншоты чека · {photos.length}/{MAX_PHOTOS}</span>
            <div className={css.photos}>
              {photos.map((p, i) => (
                <div key={p.url} className={css.ph}>
                  <img src={p.url} alt="" />
                  <button type="button" className={css.phX} aria-label="Убрать фото" onClick={() => removePhoto(i)}>✕</button>
                </div>
              ))}
              {photos.length < MAX_PHOTOS ? <button type="button" className={css.phAdd} onClick={() => fileRef.current?.click()}>+ фото</button> : null}
            </div>
            <input ref={fileRef} className={css.hidden} type="file" accept="image/*" multiple onChange={addFiles} />
          </div>
          <div className={css.field}>
            <span className={css.fLbl}>Комментарий</span>
            <button type="button" className={`${css.chip} ${comment ? "" : css.chipEmpty}`} onClick={() => setPad("comment")}>{comment || "необязательно…"}</button>
          </div>
          {err ? <div className={css.err}>{err}</div> : null}
        </div>
        <div className={css.foot}>
          <button type="button" className={`${css.btn} ${css.btnCancel}`} disabled={busy} onClick={dismiss}>Отмена</button>
          <button type="button" className={`${css.btn} ${css.btnGo}`} disabled={!canSend} onClick={() => void send()}>{busy ? "Отправляю…" : "Отправить на подтверждение"}</button>
        </div>
      </SheetDialog>
      <Dropdown open={!!dd} anchor={dd} multi={false} title="Расходники" selectedIds={consId ? [consId] : []}
        action={{ label: "+ свой вариант…", onPress: () => setPad("item") }}
        items={(catalog ?? []).map((c) => ({ id: c.id, label: c.name, meta: c.unit || undefined }))}
        onCommit={(ids) => { const c = catalog?.find((x) => x.id === String(ids[0] ?? "")); if (c) { setConsId(c.id); setItem(c.name); } }}
        onClose={() => setDd(null)} />
      <NumPadPop open={!!np} anchor={np} label="Сумма покупки" mode="money" value={amount || ""} max={999999}
        onCommit={(v) => setAmount(Math.max(0, Math.round(v)))} onClose={() => setNp(null)} />
      <TextPad open={pad === "item"} label="Что куплено" value={item} maxLength={120} placeholder="Например: батарейки AA, 8 шт"
        onCommit={(v) => { const t = v.trim(); if (t) { setItem(t); setConsId(null); } }} onClose={() => setPad(null)} />
      <TextPad open={pad === "comment"} label="Комментарий к покупке" value={comment} maxLength={500} multiline placeholder="Зачем, где купил…"
        onCommit={(v) => setComment(v.trim())} onClose={() => setPad(null)} />
    </>
  );
}
